import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useLanguage } from "../../contexts/LanguageContext";
import { useTheme } from "../../contexts/ThemeContext";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  LayoutDashboard,
  Link2,
  Store,
  Settings,
  LogOut,
  Sun,
  Moon,
  Globe,
  Menu,
  X,
  ChevronRight,
  Wallet,
  Receipt,
  Building2,
  Headphones,
} from "lucide-react";
import { useState } from "react";

export default function DashboardLayout({ children }) {
  const { user, logout } = useAuth();
  const { t, language, changeLanguage, languages } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navItems = [
    { path: "/dashboard", icon: LayoutDashboard, label: t("nav.dashboard"), description: t("nav.dashboardDesc") },
    { path: "/payment-links", icon: Link2, label: t("nav.paymentLinks"), description: t("nav.paymentLinksDesc") },
    { path: "/withdraw", icon: Wallet, label: t("nav.withdraw"), description: t("nav.withdrawDesc") },
    { path: "/store", icon: Store, label: t("nav.store"), description: t("nav.storeDesc") },
    ...(user?.is_platform_admin ? [
      { path: "/commission", icon: Receipt, label: t("commission.title"), description: t("commission.subtitle") },
      { path: "/admin/withdrawals", icon: Building2, label: t("adminWithdraw.title"), description: t("adminWithdraw.subtitle") },
    ] : []),
    { path: "/settings", icon: Settings, label: t("nav.settings"), description: t("nav.settingsDesc") },
    { path: "/support", icon: Headphones, label: t("support.title"), description: t("support.subtitle") },
  ];

  const handleLogout = async () => {
    await logout();
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden transition-opacity duration-300"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50 w-72 bg-card border-r border-border flex flex-col transition-all duration-300 ease-out ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        {/* Logo - Text only, no icon */}
        <div className="h-20 flex items-center justify-between px-6 border-b border-border">
          <Link to="/dashboard" className="flex items-center group">
            <span className="text-2xl font-black tracking-tight bg-gradient-to-r from-[#0B1F3A] to-[#3A6D8C] bg-clip-text text-transparent dark:from-white dark:to-slate-300">
              Ellalink
            </span>
          </Link>
          <button
            className="lg:hidden p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-200"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto p-4 space-y-2">
          <p className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            {t("nav.menu")}
          </p>
          {navItems.map((item, index) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`group flex items-center gap-3 px-3 py-2.5 lg:px-4 lg:py-3.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                  isActive
                    ? "bg-gradient-to-r from-[#0B1F3A] to-[#1A365D] text-white shadow-lg shadow-[#0B1F3A]/20 dark:from-white dark:to-slate-200 dark:text-[#0B1F3A]"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted/80"
                }`}
                style={{ animationDelay: `${index * 50}ms` }}
                data-testid={`nav-${item.path.slice(1)}`}
              >
                <div className={`p-1.5 lg:p-2 rounded-lg transition-all duration-200 ${
                  isActive 
                    ? "bg-white/20 dark:bg-[#0B1F3A]/20" 
                    : "bg-muted group-hover:bg-background"
                }`}>
                  <item.icon className="h-5 w-5" />
                </div>
                <div className="flex-1">
                  <span className="block">{item.label}</span>
                  <span className={`text-xs hidden lg:block ${isActive ? "text-white/70 dark:text-[#0B1F3A]/70" : "text-muted-foreground"}`}>
                    {item.description}
                  </span>
                </div>
                {isActive && (
                  <ChevronRight className="h-4 w-4 opacity-70" />
                )}
              </Link>
            );
          })}
        </nav>

        {/* User section */}
        <div className="p-4 border-t border-border">
          <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-muted/50 mb-3">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#0B1F3A] to-[#3A6D8C] flex items-center justify-center text-white font-bold text-lg shadow-md">
              {user?.name?.charAt(0).toUpperCase() || "U"}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold truncate">{user?.name}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            className="w-full justify-start text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-all duration-200"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="h-4 w-4 mr-3" />
            {t("nav.logout")}
          </Button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="h-20 bg-card/80 backdrop-blur-xl border-b border-border flex items-center justify-between px-4 lg:px-8 sticky top-0 z-30">
          <button
            className="lg:hidden p-2.5 rounded-xl text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-200"
            onClick={() => setSidebarOpen(true)}
            data-testid="mobile-menu-btn"
          >
            <Menu className="h-6 w-6" />
          </button>

          <div className="flex-1" />

          <div className="flex items-center gap-2">
            {/* Language switcher */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="rounded-xl hover:bg-muted transition-all duration-200"
                  data-testid="language-switcher"
                >
                  <Globe className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-40">
                {languages.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => changeLanguage(lang.code)}
                    className={`cursor-pointer transition-colors ${language === lang.code ? "bg-muted font-medium" : ""}`}
                    data-testid={`lang-${lang.code}`}
                  >
                    <span className="mr-3 text-lg">{lang.flag}</span>
                    {lang.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Theme toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="rounded-xl hover:bg-muted transition-all duration-200"
              data-testid="theme-toggle"
            >
              {theme === "light" ? (
                <Moon className="h-5 w-5" />
              ) : (
                <Sun className="h-5 w-5" />
              )}
            </Button>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto p-3 sm:p-4 lg:p-8">{children}</main>
      </div>
    </div>
  );
}
